import styled, { css } from 'styled-components';

/**
 * Layout primitives for the requirements workspace.
 *
 * Everything visual comes from theme tokens or Orion components so these
 * screens sit alongside the rest of Columbia rather than looking bolted on.
 */

/**
 * Page frame for list and detail screens. Matches the Benefits screen wrapper
 * so the workspace lines up with the rest of Columbia.
 *
 * Use `$noTopPad` on screens that open with a `BackHeader` — the component
 * manages its own vertical spacing internally and expects to sit flush at the
 * top of its container.
 */
export const Page = styled.div<{ $noTopPad?: boolean }>`
  ${({ theme, $noTopPad }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(5)};
    padding: ${$noTopPad
      ? `0 ${theme.spacing(5)} ${theme.spacing(5)}`
      : theme.spacing(5)};
  `}
`;

export const Stack = styled.div<{ $gap?: number }>`
  ${({ theme, $gap = 3 }) => css`
    display: grid;
    /* An auto track is sized to its widest item, and every sibling then
       stretches to match — so one wide child (the payload preview's pricing
       section) pushed the whole column out from under the page's side panel.
       Capping the track keeps overflow the wide child's own problem. */
    grid-template-columns: minmax(0, 1fr);
    gap: ${theme.spacing($gap)};
    align-content: start;
  `}
`;

export const Row = styled.div<{ $gap?: number; $wrap?: boolean }>`
  ${({ theme, $gap = 2, $wrap = true }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing($gap)};
    ${$wrap && 'flex-wrap: wrap;'}
  `}
`;

export const Cards = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: ${theme.spacing(3)};
  `}
`;

export const Panel = styled.section`
  ${({ theme }) => css`
    background: ${theme.palette.neutral['00']};
    border: 1px solid ${theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing(3)};
  `}
`;

export const Muted = styled.div`
  ${({ theme }) => css`
    color: ${theme.palette.neutral[80]};
    font-size: 0.8125rem;
  `}
`;

export const Code = styled.code`
  ${({ theme }) => css`
    font-family: monospace;
    font-size: 0.75rem;
    color: ${theme.palette.neutral[80]};
  `}
`;

export const Pre = styled.pre`
  ${({ theme }) => css`
    background: ${theme.palette.neutral[10]};
    border-radius: ${theme.borderRadius.sm};
    padding: ${theme.spacing(2)};
    overflow: auto;
    max-height: 420px;
    font-size: 0.75rem;
    line-height: 1.5;
    margin: 0;
  `}
`;

export const Metadata = styled.dl`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: ${theme.spacing(1)} ${theme.spacing(3)};
    margin: 0;

    dt {
      color: ${theme.palette.neutral[80]};
      font-size: 0.8125rem;
    }

    dd {
      margin: 0;
      font-size: 0.8125rem;
    }
  `}
`;

/**
 * Why a change cannot be decided. The API returns a code so any client can
 * branch on it; the sentence is presentation, so it lives here.
 */
const DECIDABLE_REASON: Record<string, string> = {
  CHANGE_NOT_PENDING:
    'This change has already been decided, so there is nothing to approve or reject.',
  CHANGE_NOT_RUN_LINKED:
    'This change is not part of a configuration run, so it cannot be decided here.',
  RUN_NOT_ACTIVE:
    'The run this change belongs to has finished, so it can no longer be decided.',
};

export const decidableReasonText = (reason: string | null) =>
  (reason && DECIDABLE_REASON[reason]) ??
  'This change is not awaiting a decision.';
